/**
 * What this site actually does with a visitor's data — the facts the legal
 * generators turn into prose.
 *
 * ⚠️ THESE ARE CLAIMS ABOUT A SYSTEM, NOT COPY. Change the site, change this
 * file, rebuild — do not edit the rendered prose to match. Every flag below was
 * verified against the running stack on 2026-09-04 rather than assumed, because
 * the fleet's legal pages have now shipped three separate false sentences, each
 * of which came from a flag that described an older version of the site.
 *
 * ⚠️ UNLIKE THE CMS CLIENT SITES, THIS FILE IS THE ONLY SOURCE. There is no
 * seeded CMS copy for Fairmeadow, so `pages/Privacy.tsx` reads this at render
 * time and a correction here reaches the live site on the next deploy. That is
 * simpler than the client-owned model and it is also less forgiving: nothing
 * else will notice if a flag stops being true.
 */
import type { PrivacyPolicyOptions } from '@gjl/site-kit'

const LAST_UPDATED = 'September 2026'

export const PRIVACY_OPTIONS: PrivacyPolicyOptions = {
  orgName: 'Fairmeadow Capital',
  siteUrl: 'https://fairmeadow.capital',
  contactEmail: 'garrett@fairmeadow.capital',
  lastUpdated: LAST_UPDATED,

  collectsContactForm: true,

  // ⚠️ 'relay', AND NEITHER OTHER VALUE WOULD HAVE BEEN TRUE.
  //
  // `server/src/handlers/contact.ts` validates, checks the honeypot, verifies
  // the captcha, then calls `emailService.sendEmail` and returns. There is no
  // DynamoDB client, no repository and no table anywhere in `server/` — grep it.
  //
  // So 'mailto' would deny that the message reaches a server of ours (it does,
  // over HTTPS, through API Gateway to a Lambda) and 'server' would promise a
  // stored record that a subject-access request could retrieve (there is none —
  // what exists is an email in Garrett's inbox). Picking the nearer of two false
  // options is exactly how the three earlier false sentences happened.
  contactFormMode: 'relay',

  // Google reCAPTCHA v2 checkbox on BOTH forms — /contact and /fit.
  // `CONTACT_CAPTCHA_REQUIRED` is "true" on `fairmeadow-capital-prod-api` and
  // `verifyCaptcha` fails closed, so this is not aspirational.
  //
  // ⚠️ THIS FLAG IS WHAT OBLIGES THE DISCLOSURE. reCAPTCHA sets cookies, which
  // falsifies the default "no tracking cookies" line — see
  // docs/conventions/form-spam-protection.md.
  captcha: 'recaptcha',

  // GA4 G-CYSL89QH6Z, set in `lib/analytics.ts` for prod builds. It sets
  // cookies, so the generated "no analytics" copy would be false.
  analytics: 'google',

  // Hosted on AWS in us-west-1. Fairmeadow's enquiries come from outside the US
  // often enough that the cross-border paragraph is not decorative.
  dataProcessedIn: 'the United States',

  // No newsletter and no on-site payments. Investor distributions and any
  // capital movement happen outside this website.
}

export const ACCESSIBILITY_OPTIONS = {
  orgName: 'Fairmeadow Capital',
  contactEmail: 'garrett@fairmeadow.capital',
  lastUpdated: LAST_UPDATED,
}
